// @ts-nocheck
import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HeroLayout1Data } from '../layout_1/hero-layout-1.component';

/**
 * Preview component for the "Hero Layout 3" template used in the website builder — renders hero content (image, title, paragraph, CTA) with configurable styling and emits button click events.
 * @component
 * @selector app-hero-layout-3-preview
 * @example
 *   <app-hero-layout-3-preview
 *     [data]="sampleHeroData"
 *     [headingColor]="'#222'"
 *     [paragraphColor]="'#666'"
 *     [primaryColor]="'#007bff'"
 *     [headingFont]="'Arial, sans-serif'"
 *     [bodyFont]="'Roboto, sans-serif'"
 *     [titleStyles]="{ 'font-weight': '700' }"
 *     (buttonClick)="onHeroButtonClick($event)">
 *   </app-hero-layout-3-preview>
 * @input {HeroLayout1Data | null} data - The hero content payload, including title, subtitle, CTA(s) and image metadata (image.src, image.alt, image.scaletype) or legacy imageUrl.
 * @input {string | null} headingColor - Optional CSS color for the hero heading text.
 * @input {string | null} paragraphColor - Optional CSS color for the hero paragraph text.
 * @input {string | null} headingFont - Optional CSS font-family for the hero heading.
 * @input {string | null} bodyFont - Optional CSS font-family for the hero body/paragraph.
 * @input {string | null} primaryColor - Optional primary color used for CTAs and accents.
 * @input {any} titleStyles - Additional inline style overrides applied to the title element.
 * @output {EventEmitter<{ link: string; event: Event }>} buttonClick - Emitted when a CTA button is activated; payload includes the target link and the originating DOM event.
 *
 * Key methods:
 * - onButtonClick(link: string | undefined, event: Event): void
 *     Emits the buttonClick event only when a valid link is provided; used by CTA elements to forward user interaction.
 * - getImageSrc(): string
 *     Resolves the image source from data.image.src or the legacy data.imageUrl, returning an empty string when not available.
 * - getImageAlt(): string
 *     Returns the image alt text from data.image.alt or a sensible default ('Car Wash') if none is provided.
 * - getObjectFit(): string
 *     Maps the configured image.scaletype ('fill' | 'fit' | 'stretch') to the corresponding CSS object-fit value ('cover' | 'contain' | 'fill').
 * - getImageStyles(): any
 *     Produces style entries (currently object-fit) for the image element based on the selected scaletype.
 *
 * Important lifecycle hooks:
 * - This standalone preview component does not require or implement Angular lifecycle hooks itself; it is a presentational component that reacts to input property changes and emits user interactions via buttonClick.
 */
@Component({
  selector: 'app-hero-layout-3-preview',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './hero-layout-3.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./hero-layout-3.component.scss'],
})
export class HeroLayout3PreviewComponent {
  @Input() data: HeroLayout1Data | null = null;
  @Input() headingColor: string | null = null;
  @Input() paragraphColor: string | null = null;
  @Input() headingFont: string | null = null;
  @Input() bodyFont: string | null = null;
  @Input() primaryColor: string | null = null;
  @Input() titleStyles: any = {};
  @Output() buttonClick = new EventEmitter<{ link: string; event: Event }>();

  onButtonClick(link: string | undefined, event: Event): void {
    if (link) {
      this.buttonClick.emit({ link, event });
    }
  }

  getImageSrc(): string {
    return this.data?.image?.src || this.data?.imageUrl || '';
  }

  getImageAlt(): string {
    return this.data?.image?.alt || 'Car Wash';
  }

  /**
   * Determine the appropriate CSS object-fit value based on the component's image scaletype.
   * @example
   * etObjectFit()
   * 'contain'
   * @param {void} none - No arguments.
   * @returns {string} CSS object-fit value ('contain', 'cover', or 'fill').
   **/
  getObjectFit(): string {
    const scaletype = this.data?.image?.scaletype || 'fit';
    // Map scale types to CSS object-fit values
    switch (scaletype) {
      case 'fill':
        return 'cover'; // Fill: fills container, maintains aspect ratio, may crop
      case 'fit':
        return 'contain'; // Fit: fits within container, maintains aspect ratio, no cropping
      case 'stretch':
        return 'fill'; // Stretch: stretches to fill container exactly, may distort
      default:
        return 'contain';
    }
  }

  /**
   * Compute and return inline styles for the hero image element (sets object-fit).
   * @example
   * etImageStyles()
   * { "object-fit": "contain" }
   * @returns {{any}} Returns an object containing CSS style properties for the image (for example 'object-fit').
   **/
  getImageStyles(): any {
    const baseStyles: any = {
      'object-fit': this.getObjectFit(),
    };

    // For all types, the image should fill its container
    // The CSS already sets width: 100% and height: 100% on .car-illustration
    // The difference is in object-fit behavior

    return baseStyles;
  }
}
